// BHP-1083: Tailwind confirm dialog for registering (ordering) a domain.
// Replaces the react-bootstrap DomainConfirmation Modal. Registration is async
// on the backend: on success we navigate to the new domain's detail page (which
// polls the pending → registered/failed status), so the user always lands on a
// page that shows the outcome. A synchronous failure (e.g. taken/422) is shown
// inline so the user can retry or cancel.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type DomainAvailability, formatAmount, tldOf } from '../domain';
import { useRegisterDomain } from '../application/domainMutations';
import { toUserMessage } from '../../../shared/http/errorMessage';
import { useDialogA11y } from '../../../shared/ui/useDialogA11y';

/**
 * BHP-1127: the same two amounts the results list showed, worded the same way.
 * This is the last screen before the order goes out, so it may not tell the
 * customer less than the row he clicked.
 */
function Prices({
  availability
}: {
  availability: DomainAvailability;
}): JSX.Element {
  const registration = formatAmount(
    availability.priceCurrency,
    availability.priceAmount
  );
  const renewal = formatAmount(
    availability.priceCurrency,
    availability.renewalPriceAmount
  );

  if (registration !== null && registration === renewal) {
    return (
      <p className="mt-2 text-base font-semibold text-slate-900">
        {registration} / year
      </p>
    );
  }

  return (
    <>
      <p className="mt-2 text-base font-semibold text-slate-900">
        {registration === null
          ? 'Price unavailable'
          : `${registration} first year`}
      </p>
      <p className="text-sm text-slate-500">
        {renewal === null
          ? 'Renewal price unknown.'
          : `Then ${renewal} per year.`}
      </p>
    </>
  );
}

export default function RegisterDomainModal({
  sld,
  availability,
  onClose
}: {
  sld: string;
  availability: DomainAvailability;
  onClose: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const { register, isPending } = useRegisterDomain();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dialogRef = useDialogA11y<HTMLDivElement>(onClose);

  const onConfirm = async (): Promise<void> => {
    setErrorMessage(null);
    const tld = tldOf(availability.domain, sld);
    if (tld === null) {
      setErrorMessage(
        `Could not determine the extension of ${availability.domain}.`
      );
      return;
    }
    try {
      const domain = await register({ domainName: sld, tld });
      // The detail page polls the async registration status, so the user lands
      // on a page that shows the outcome either way.
      navigate(`/domain/details/${domain.uuid}`);
    } catch (error) {
      setErrorMessage(toUserMessage(error, 'Registration failed.'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="register-domain-title"
        className="w-full max-w-md overflow-hidden rounded-xl border border-solid border-slate-200 bg-white shadow-xl"
      >
        <div className="border-0 bg-slate-50 px-5 py-3">
          <h3
            id="register-domain-title"
            className="text-base font-semibold text-slate-900"
          >
            Confirm registration
          </h3>
        </div>

        <div className="px-5 py-4 text-sm text-slate-700">
          <p>
            Register <b>{availability.domain}</b> for the active customer?
          </p>
          <Prices availability={availability} />
          {errorMessage !== null ? (
            <p
              role="alert"
              className="mt-3 rounded border border-solid border-rose-200 bg-rose-50 p-2 text-rose-700"
            >
              {errorMessage}
            </p>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 bg-slate-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              void onConfirm();
            }}
            disabled={isPending}
            className="rounded-md border-0 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
          >
            {isPending ? 'Registering…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
